/**
 * AGRÉGATS de la compétition calculés EN TEMPS RÉEL depuis l'état officiel (flux SSE) :
 *  - totaux globaux (matchs, buts, xG, cartons…),
 *  - une ligne par ÉQUIPE (cumul de ses matchs),
 *  - une ligne par MATCH.
 * Les matchs EN COURS sont intégrés et signalés (`live`). Pur → testable hors DOM.
 */
import type { OfficialState, MatchView } from "./officialStore.ts";

/** Codes [domicile, extérieur] d'un match (groupe : calendrier ; KO : bracket résolu). */
function matchTeams(state: OfficialState, v: MatchView): [string | null, string | null] {
  if (v.match.stage === "group") return [v.match.home, v.match.away];
  const n = state.bracket["M" + String(v.match.id)];
  return [n?.a ?? null, n?.b ?? null];
}

const num = (v: number | null | undefined): number => (typeof v === "number" ? v : 0);

export interface TeamStat {
  readonly code: string;
  readonly group: string;
  readonly played: number;
  readonly minutes: number; // 90/match, 120 si prolongation, minute en cours si live
  readonly goalsFor: number;
  readonly goalsAgainst: number;
  readonly diff: number;
  readonly points: number;
  readonly xg: number;
  readonly xga: number;
  readonly shots: number;
  readonly shotsOn: number;
  readonly possession: number | null;
  readonly corners: number;
  readonly fouls: number;
  readonly yellow: number;
  readonly red: number;
  readonly elo: number;
  readonly live: boolean;
}

export interface MatchStat {
  readonly id: number;
  readonly home: string;
  readonly away: string;
  readonly stage: string; // "group" | "r32" | …
  readonly group: string | null;
  readonly matchday: number | null;
  readonly date: string;
  readonly status: MatchView["status"];
  readonly live: boolean;
  readonly homeGoals: number | null;
  readonly awayGoals: number | null;
  readonly goals: number | null;
  readonly xg: number | null; // xG total du match
  readonly shots: number | null;
  readonly cards: number | null; // jaunes + rouges
  // Stats PAR ÉQUIPE (ordre du match : [home]=1re équipe affichée, [away]=2e) — pas de
  // notion domicile/extérieur en CdM (lieux neutres), juste l'ordre du calendrier.
  readonly passHome: number | null; // % passes réussies, 1re équipe
  readonly passAway: number | null; // % passes réussies, 2e équipe
  readonly xgHome: number | null;
  readonly xgAway: number | null;
  readonly shotsHome: number | null; // tirs tentés
  readonly shotsAway: number | null;
  readonly onTargetHome: number | null; // tirs cadrés
  readonly onTargetAway: number | null;
  readonly possHome: number | null; // possession %
  readonly possAway: number | null;
}

export interface GroupStat {
  readonly group: string;
  readonly played: number;
  readonly total: number; // matchs de groupe programmés (6)
  readonly goals: number;
  readonly goalsPerMatch: number | null;
  readonly xg: number;
  readonly shots: number;
  readonly corners: number;
  readonly yellow: number;
  readonly red: number;
  readonly live: boolean;
  readonly complete: boolean;
}

export interface TournamentTotals {
  readonly matchesPlayed: number;
  readonly matchesTotal: number;
  readonly liveNow: number;
  readonly goals: number;
  readonly goalsPerMatch: number | null;
  readonly xg: number;
  readonly shots: number;
  readonly corners: number;
  readonly yellow: number;
  readonly red: number;
}

export interface TournamentStats {
  readonly totals: TournamentTotals;
  readonly groups: readonly GroupStat[];
  readonly teams: readonly TeamStat[];
  readonly matches: readonly MatchStat[];
}

interface GroupAcc {
  played: number;
  goals: number;
  xg: number;
  shots: number;
  corners: number;
  yellow: number;
  red: number;
  live: boolean;
}

interface TeamAcc {
  played: number;
  minutes: number;
  gf: number;
  ga: number;
  xg: number;
  xga: number;
  shots: number;
  shotsOn: number;
  possSum: number;
  possN: number;
  corners: number;
  fouls: number;
  yellow: number;
  red: number;
  live: boolean;
}

export function computeTournamentStats(state: OfficialState): TournamentStats {
  // index points/diff de classement par équipe
  const standBy = new Map<string, { points: number; group: string }>();
  for (const s of state.standings) for (const r of s.rows) standBy.set(r.team, { points: r.points, group: s.group });

  const acc = new Map<string, TeamAcc>();
  const team = (c: string): TeamAcc =>
    acc.get(c) ??
    (acc
      .set(c, { played: 0, minutes: 0, gf: 0, ga: 0, xg: 0, xga: 0, shots: 0, shotsOn: 0, possSum: 0, possN: 0, corners: 0, fouls: 0, yellow: 0, red: 0, live: false })
      .get(c) as TeamAcc);

  const matches: MatchStat[] = [];
  const gTotal = new Map<string, number>(); // matchs de groupe programmés par groupe
  const gAcc = new Map<string, GroupAcc>();
  const grp = (g: string): GroupAcc =>
    gAcc.get(g) ?? (gAcc.set(g, { played: 0, goals: 0, xg: 0, shots: 0, corners: 0, yellow: 0, red: 0, live: false }).get(g) as GroupAcc);
  let gPlayed = 0;
  let gLive = 0;
  let gGoals = 0;
  let gXg = 0;
  let gShots = 0;
  let gCorners = 0;
  let gY = 0;
  let gR = 0;

  for (const v of state.matches) {
    const [h, a] = matchTeams(state, v);
    const isLive = v.status === "live" || v.status === "paused";
    const ft = v.displayScore ?? v.live?.score?.ft ?? null;
    const played = v.status === "finished" || (isLive && ft != null);
    const st = v.live?.stats;
    const sv = (key: string, side: 0 | 1): number | null => st?.[key]?.[side] ?? null;
    if (v.match.stage === "group" && v.match.group) gTotal.set(v.match.group, (gTotal.get(v.match.group) ?? 0) + 1);

    if (played) {
      gPlayed += 1;
      if (isLive) gLive += 1;
      const hg = ft ? ft[0] : null;
      const ag = ft ? ft[1] : null;
      const mgoals = num(hg) + num(ag);
      gGoals += mgoals;
      const mxg = sv("xg", 0) != null || sv("xg", 1) != null ? num(sv("xg", 0)) + num(sv("xg", 1)) : null;
      const mshots = sv("shots", 0) != null || sv("shots", 1) != null ? num(sv("shots", 0)) + num(sv("shots", 1)) : null;
      const mcorners = num(sv("corners", 0)) + num(sv("corners", 1));
      const my = num(sv("yellow", 0)) + num(sv("yellow", 1));
      const mr = num(sv("red", 0)) + num(sv("red", 1));
      const mcards = my + mr;
      gXg += num(mxg);
      gShots += num(mshots);
      gCorners += mcorners;
      gY += my;
      gR += mr;

      if (v.match.stage === "group" && v.match.group) {
        const ga = grp(v.match.group);
        ga.played += 1;
        ga.goals += mgoals;
        ga.xg += num(mxg);
        ga.shots += num(mshots);
        ga.corners += mcorners;
        ga.yellow += my;
        ga.red += mr;
        if (isLive) ga.live = true;
      }

      matches.push({
        id: v.match.id,
        home: h ?? v.match.home,
        away: a ?? v.match.away,
        stage: v.match.stage,
        group: v.match.group,
        matchday: v.match.matchday ?? null,
        date: v.match.utcDateTime,
        status: v.status,
        live: isLive,
        homeGoals: hg,
        awayGoals: ag,
        goals: ft ? num(hg) + num(ag) : null,
        xg: mxg,
        shots: mshots,
        cards: mcards,
        passHome: sv("passAcc", 0),
        passAway: sv("passAcc", 1),
        xgHome: sv("xg", 0),
        xgAway: sv("xg", 1),
        shotsHome: sv("shots", 0),
        shotsAway: sv("shots", 1),
        onTargetHome: sv("shotsOnTarget", 0),
        onTargetAway: sv("shotsOnTarget", 1),
        possHome: sv("possession", 0),
        possAway: sv("possession", 1),
      });

      // minutes du match : en cours = minute actuelle ; sinon 90, ou 120 si prolongation.
      // L'objet extraTime est TOUJOURS présent (placeholder [0,0]) → on ne peut PAS s'y
      // fier ; une prolongation n'a eu lieu que s'il y a des buts en prolongation ou des
      // tirs au but (un 0-0 a.p. se décide forcément aux tab).
      // base : minute en cours si live, sinon 90 (120 avec prolongation) ; + arrêts de
      // jeu (status.extra en direct, sinon estimés depuis le dernier évènement).
      const et = v.live?.extraTime?.score;
      const wentET = v.penalties != null || (et != null && (num(et[0]) > 0 || num(et[1]) > 0));
      const mmin = (isLive ? num(v.minute) : wentET ? 120 : 90) + num(v.addedTime);

      // cumul par équipe (côté 0 = home, 1 = away)
      const sides: Array<[string | null, 0 | 1, 0 | 1]> = [
        [h, 0, 1],
        [a, 1, 0],
      ];
      for (const [code, me, opp] of sides) {
        if (!code) continue;
        const t = team(code);
        t.played += 1;
        t.minutes += mmin;
        if (isLive) t.live = true;
        t.gf += me === 0 ? num(hg) : num(ag);
        t.ga += me === 0 ? num(ag) : num(hg);
        t.xg += num(sv("xg", me));
        t.xga += num(sv("xg", opp));
        t.shots += num(sv("shots", me));
        t.shotsOn += num(sv("shotsOnTarget", me));
        t.corners += num(sv("corners", me));
        t.fouls += num(sv("fouls", me));
        t.yellow += num(sv("yellow", me));
        t.red += num(sv("red", me));
        const poss = sv("possession", me);
        if (poss != null) {
          t.possSum += poss;
          t.possN += 1;
        }
      }
    }
  }

  const teams: TeamStat[] = [...acc.entries()].map(([code, t]) => ({
    code,
    group: standBy.get(code)?.group ?? "",
    played: t.played,
    minutes: t.minutes,
    goalsFor: t.gf,
    goalsAgainst: t.ga,
    diff: t.gf - t.ga,
    points: standBy.get(code)?.points ?? 0,
    xg: t.xg,
    xga: t.xga,
    shots: t.shots,
    shotsOn: t.shotsOn,
    possession: t.possN ? t.possSum / t.possN : null,
    corners: t.corners,
    fouls: t.fouls,
    yellow: t.yellow,
    red: t.red,
    elo: Math.round(state.currentElos[code] ?? 0),
    live: t.live,
  }));

  const groups: GroupStat[] = [...gTotal.keys()].sort().map((g) => {
    const a = gAcc.get(g);
    const played = a?.played ?? 0;
    const total = gTotal.get(g) ?? 0;
    return {
      group: g,
      played,
      total,
      goals: a?.goals ?? 0,
      goalsPerMatch: played ? (a?.goals ?? 0) / played : null,
      xg: a?.xg ?? 0,
      shots: a?.shots ?? 0,
      corners: a?.corners ?? 0,
      yellow: a?.yellow ?? 0,
      red: a?.red ?? 0,
      live: a?.live ?? false,
      complete: total > 0 && played >= total,
    };
  });

  return {
    totals: {
      matchesPlayed: gPlayed,
      matchesTotal: state.matches.length,
      liveNow: gLive,
      goals: gGoals,
      goalsPerMatch: gPlayed ? gGoals / gPlayed : null,
      xg: gXg,
      shots: gShots,
      corners: gCorners,
      yellow: gY,
      red: gR,
    },
    groups,
    teams,
    matches,
  };
}
